import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type OnsiteContent = {
  id: string;
  hero_image_url: string | null;
  hero_kicker: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  intro_body: string | null;
  tab_label: string | null;
};

export type OnsiteGalleryImage = { id: string; url: string };

export type OnsiteAmenity = {
  id: string;
  slug: string;
  label: string;
  cover_image_url: string | null;
  description: string | null;
  gallery: OnsiteGalleryImage[];
  position: number;
};

export const ONSITE_GALLERY_IMAGE_TABLE = "onsite_amenity_gallery";


function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const CONTENT_COLS =
  "id,hero_image_url,hero_kicker,hero_title,hero_subtitle,intro_body,tab_label";
const AMENITY_COLS =
  "id,slug,label,cover_image_url,description,gallery,position";

const pageDataSchema = z
  .object({ experience_slug: z.string().min(1).max(60).optional() })
  .optional();

export const getOnsitePageData = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => pageDataSchema.parse(i) ?? {})
  .handler(
    async ({
      data,
    }): Promise<{
      content: OnsiteContent | null;
      amenities: OnsiteAmenity[];
    }> => {
      const sb = publicClient();
      const expSlug = data?.experience_slug ?? null;

      const [c, a, ov, gx, exp] = await Promise.all([
        sb
          .from("onsite_content")
          .select(CONTENT_COLS)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle(),
        sb
          .from("onsite_amenities")
          .select(AMENITY_COLS)
          .order("position", { ascending: true }),
        sb
          .from("page_overrides")
          .select("field_key,experience_id,value_text,value_url")
          .eq("page_key", "onsite"),
        sb
          .from("gallery_image_experiences")
          .select("image_table,image_id,experience_id")
          .eq("image_table", ONSITE_GALLERY_IMAGE_TABLE),
        expSlug
          ? sb.from("experiences").select("id").eq("slug", expSlug).maybeSingle()
          : Promise.resolve({ data: null, error: null } as const),
      ]);

      if (c.error) throw new Error(c.error.message);
      if (a.error) throw new Error(a.error.message);
      if (ov.error) throw new Error(ov.error.message);
      if (gx.error) throw new Error(gx.error.message);
      if ("error" in exp && exp.error) throw new Error(exp.error.message);

      const expId =
        ("data" in exp && exp.data ? (exp.data as { id: string }).id : null) ||
        null;

      let content = (c.data ?? null) as OnsiteContent | null;
      const amenitiesRaw = (a.data ?? []) as Array<
        Omit<OnsiteAmenity, "gallery"> & { gallery: unknown }
      >;
      let amenities: OnsiteAmenity[] = amenitiesRaw.map((row) => ({
        ...row,
        gallery: Array.isArray(row.gallery)
          ? (row.gallery as unknown[])
              .map((v): OnsiteGalleryImage | null => {
                if (
                  v &&
                  typeof v === "object" &&
                  typeof (v as { id?: unknown }).id === "string" &&
                  typeof (v as { url?: unknown }).url === "string"
                ) {
                  return {
                    id: (v as { id: string }).id,
                    url: (v as { url: string }).url,
                  };
                }
                return null;
              })
              .filter((v): v is OnsiteGalleryImage => v !== null)
          : [],
      }));


      if (expId) {
        const byField = new Map<
          string,
          { text: string | null; url: string | null }
        >();
        for (const o of ov.data ?? []) {
          if (o.experience_id === expId) {
            byField.set(o.field_key, { text: o.value_text, url: o.value_url });
          }
        }
        const pick = (key: string, fallback: string | null) => {
          const v = byField.get(key);
          if (!v) return fallback;
          return v.text ?? v.url ?? fallback;
        };

        if (content) {
          content = {
            ...content,
            hero_image_url: pick("hero_image", content.hero_image_url),
            hero_kicker: pick("hero_kicker", content.hero_kicker),
            hero_title: pick("hero_title", content.hero_title),
            hero_subtitle: pick("hero_subtitle", content.hero_subtitle),
            intro_body: pick("intro_body", content.intro_body),
            tab_label: pick("tab_label", content.tab_label),
          };
        }

        amenities = amenities.map((am) => ({
          ...am,
          label: pick(`amenity:${am.slug}:label`, am.label) ?? am.label,
          cover_image_url: pick(
            `amenity:${am.slug}:cover`,
            am.cover_image_url,
          ),
          description: pick(
            `amenity:${am.slug}:description`,
            am.description,
          ),
        }));

        const byImage = new Map<string, Set<string>>();
        for (const l of gx.data ?? []) {
          const s = byImage.get(l.image_id) ?? new Set<string>();
          s.add(l.experience_id);
          byImage.set(l.image_id, s);
        }
        amenities = amenities.map((am) => ({
          ...am,
          gallery: am.gallery.filter((img) => {
            const allow = byImage.get(img.id);
            if (!allow) return true;
            return allow.has(expId);
          }),
        }));
      }

      return { content, amenities };
    },
  );


// --- Admin ---

const contentSchema = z.object({
  id: z.string().uuid().optional(),
  hero_image_url: z.string().nullable(),
  hero_kicker: z.string().nullable(),
  hero_title: z.string().nullable(),
  hero_subtitle: z.string().nullable(),
  intro_body: z.string().nullable(),
  tab_label: z.string().nullable(),
});

export const upsertOnsiteContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => contentSchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const payload = {
      hero_image_url: data.hero_image_url,
      hero_kicker: data.hero_kicker,
      hero_title: data.hero_title,
      hero_subtitle: data.hero_subtitle,
      intro_body: data.intro_body,
      tab_label: data.tab_label,
    };
    if (data.id) {
      const { error } = await sb
        .from("onsite_content")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("onsite_content")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

const amenitySchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, and dashes only"),
  label: z.string().min(1).max(120),
  cover_image_url: z.string().nullable(),
  description: z.string().nullable(),
  gallery: z
    .array(z.object({ id: z.string().uuid(), url: z.string().url() }))
    .max(40),
});

export const upsertOnsiteAmenity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => amenitySchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("onsite_amenities")
        .update({
          slug: data.slug,
          label: data.label,
          cover_image_url: data.cover_image_url,
          description: data.description,
          gallery: data.gallery,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: existing } = await sb
      .from("onsite_amenities")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { data: row, error } = await sb
      .from("onsite_amenities")
      .insert({
        slug: data.slug,
        label: data.label,
        cover_image_url: data.cover_image_url,
        description: data.description,
        gallery: data.gallery,
        position: nextPos,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteOnsiteAmenity = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("onsite_amenities")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderOnsiteAmenities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("onsite_amenities")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });
